import axios from 'axios';
import { getAccessToken, refreshSession } from './auth';
import { toast } from '../components/ui/Toast';
import { useAuthStore, DEV_USERS, isDevMode } from '../stores/auth-store';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export const api = axios.create({
  baseURL: API_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: inject auth header
api.interceptors.request.use(async (config) => {
  if (isDevMode) {
    // Dev mode: use X-Dev-User-Id header instead of JWT
    const devRole = useAuthStore.getState().devRole;
    config.headers['X-Dev-User-Id'] = DEV_USERS[devRole];
  } else {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor: handle errors globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // 401: retry once with refreshed token (silently, no toast)
    if (status === 401 && !original._retry) {
      original._retry = true;
      try {
        const newToken = await refreshSession();
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        }
      } catch {
        // refresh failed
      }
      // Refresh failed — redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // 403: forbidden
    if (status === 403) {
      toast('error', 'No tiene permisos para realizar esta acción');
    }

    // 409: conflict (e.g. duplicate SKU, duplicate entry)
    if (status === 409) {
      const responseData = error.response?.data as { error?: { message?: string } } | undefined;
      const msg = responseData?.error?.message ?? 'El registro ya existe.';
      toast('error', msg);
    }

    // 5xx: server error
    if (status && status >= 500) {
      toast('error', 'Error del servidor. Intente nuevamente.');
    }

    // Network error (but not if it was a 401 retry that we handled above)
    if (!error.response && !original._retry) {
      toast('error', 'Error de conexión. Verifique su internet.');
    }

    return Promise.reject(error);
  },
);

/**
 * Extract error message from API response.
 */
export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? 'Error inesperado. Intente nuevamente.';
  }
  if (error instanceof Error) return error.message;
  return 'Error inesperado. Intente nuevamente.';
}
